import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, MessageSquare, Sparkles, AtSign, CheckCircle2, Clock3, CalendarClock, Reply, Hash } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { feedbackAPI } from '../../lib/api';
import { useRBAC } from '../../lib/RBACContext';

const STATUS_LABELS = {
  explanation_sent: 'Explanation Sent',
  fix_planned: 'Fix Planned',
  fixed: 'Fixed',
  closed: 'Closed',
  pending: 'Pending',
};

const TYPE_META = {
  status_update: { icon: CheckCircle2, label: 'Status Update' },
  comment: { icon: MessageSquare, label: 'Reply' },
  submission: { icon: Sparkles, label: 'Submitted' },
  status: { icon: Clock3, label: 'Status' },
};

export function NotificationCenter() {
  const navigate = useNavigate();
  const { permissions = [] } = useRBAC();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const res = await feedbackAPI.getNotifications({ limit: 100 });
        if (!mounted) return;
        setItems(res.data?.notifications || []);
      } catch {
        if (!mounted) return;
        setItems([]);
      }
    };
    load();
    return () => { mounted = false; };
  }, [permissions]);

  const storageKey = 'feedback_notification_seen';
  const seenMap = useMemo(() => {
    try { return JSON.parse(localStorage.getItem(storageKey) || '{}'); } catch { return {}; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const unreadCount = useMemo(() => {
    return items.filter((item) => {
      const lastSeen = seenMap[item.id];
      const lastActivity = item.last_message_at || item.updated_at || item.created_at;
      return !lastSeen || new Date(lastActivity).getTime() > new Date(lastSeen).getTime();
    }).length;
  }, [items, seenMap]);

  const filteredItems = useMemo(() => {
    if (filter === 'all') return items;
    if (filter === 'mentions') return items.filter((item) => item.preview?.includes('@'));
    if (filter === 'status') return items.filter((item) => ['status_update', 'status'].includes(item.type));
    if (filter === 'replies') return items.filter((item) => item.type === 'comment');
    return items;
  }, [items, filter]);

  const groupedItems = useMemo(() => {
    const buckets = { today: [], week: [], older: [] };
    const now = new Date();
    filteredItems.forEach((item) => {
      const dt = new Date(item.created_at);
      const diffDays = Math.floor((now - dt) / (1000 * 60 * 60 * 24));
      if (diffDays < 1) buckets.today.push(item);
      else if (diffDays < 7) buckets.week.push(item);
      else buckets.older.push(item);
    });
    return buckets;
  }, [filteredItems]);

  const groupedSections = [
    ['today', 'Today'],
    ['week', 'This Week'],
    ['older', 'Older'],
  ].filter(([key]) => groupedItems[key].length > 0);

  const getTypeTone = (item) => {
    if (item.preview?.includes('@')) return 'bg-violet-50 text-violet-700 border-violet-200';
    if (item.type === 'comment') return 'bg-amber-50 text-amber-700 border-amber-200';
    if (['status_update', 'status'].includes(item.type)) return 'bg-blue-50 text-blue-700 border-blue-200';
    return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  };

  const getTypeIcon = (item) => {
    if (item.preview?.includes('@')) return AtSign;
    if (item.type === 'comment') return Reply;
    if (['status_update', 'status'].includes(item.type)) return CalendarClock;
    return Hash;
  };

  const markSeen = () => {
    const next = { ...seenMap };
    items.forEach((item) => {
      next[item.id] = item.last_message_at || item.updated_at || item.created_at;
    });
    localStorage.setItem(storageKey, JSON.stringify(next));
  };

  return (
    <Popover open={open} onOpenChange={(value) => { setOpen(value); if (value) markSeen(); }}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="icon" className="relative h-10 w-10 rounded-full bg-white shadow-sm border-slate-200" data-testid="notification-center-trigger">
          <Bell className="h-4 w-4 text-slate-700" />
          {unreadCount > 0 && <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-[#800000] text-white text-[10px] font-semibold flex items-center justify-center px-1">{Math.min(unreadCount, 9)}+</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[min(360px,calc(100vw-24px))] rounded-2xl border border-slate-200 bg-white/95 backdrop-blur p-0 overflow-hidden" data-testid="notification-center-popover">
        <div className="px-4 py-3 border-b bg-slate-50 flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-slate-900">Communication Center</p>
            <p className="text-[11px] text-slate-500">{permissions.includes('admin:*') ? 'Admin feedback inbox' : 'Your feedback status and replies'}</p>
          </div>
          <Badge variant="outline" className="text-[10px]">{items.length} items</Badge>
        </div>
        <div className="px-3 py-2 border-b bg-white flex items-center gap-2 overflow-auto">
          {[
            ['all', 'All'],
            ['replies', 'Replies'],
            ['status', 'Status Updates'],
            ['mentions', 'Mentions'],
          ].map(([value, label]) => (
            <Button
              key={value}
              variant={filter === value ? 'default' : 'outline'}
              size="sm"
              className={`h-7 rounded-full text-[10px] px-3 ${filter === value ? 'bg-[#800000] hover:bg-[#9a1919] text-white' : ''}`}
              onClick={() => setFilter(value)}
              data-testid={`notification-filter-${value}`}
            >
              {label}
            </Button>
          ))}
        </div>
        <div className="max-h-[420px] overflow-auto divide-y divide-slate-100">
          {filteredItems.length === 0 ? (
            <div className="px-4 py-10 text-center text-slate-400 text-sm">No notifications yet</div>
          ) : groupedSections.map(([bucketKey, bucketLabel]) => (
            <div key={bucketKey}>
              <div className="px-4 py-2 bg-slate-50 border-y text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">{bucketLabel}</div>
              {groupedItems[bucketKey].map((item) => {
                const ToneIcon = getTypeIcon(item);
                return (
                  <button
                    key={item.id}
                    className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors"
                    onClick={() => { setOpen(false); navigate(`/feedback?thread=${item.feedback_id}`); }}
                    data-testid={`notification-item-${item.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {React.createElement(TYPE_META[item.type]?.icon || Sparkles, { className: 'h-3.5 w-3.5 text-[#800000]' })}
                          <p className="text-sm font-medium text-slate-800 truncate">{item.title}</p>
                          <Badge className="text-[10px] bg-slate-100 text-slate-700">{STATUS_LABELS[item.status] || item.status}</Badge>
                          <Badge variant="outline" className={`text-[10px] ${getTypeTone(item)}`}><ToneIcon className="h-3 w-3 mr-1" />{TYPE_META[item.type]?.label || item.type}</Badge>
                        </div>
                        <p className="text-xs text-slate-500 mt-1 line-clamp-2">{item.preview || 'Open to view details'}</p>
                        <div className="flex items-center gap-2 mt-1 text-[10px] text-slate-400">
                          <span>{item.author?.name || item.author?.email || 'System'}</span>
                          <span>•</span>
                          <span>{new Date(item.created_at).toLocaleString()}</span>
                          {item.preview?.includes('@') && <><span>•</span><AtSign className="h-3 w-3" /></>}
                        </div>
                      </div>
                      <MessageSquare className="h-4 w-4 text-slate-300 shrink-0" />
                    </div>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
        <div className="px-4 py-3 border-t bg-slate-50 flex justify-end">
          <Button variant="outline" size="sm" onClick={() => { setOpen(false); navigate('/feedback'); }} data-testid="notification-center-open-feedback">Open Communication Center</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
